import { FC, ReactNode } from "react";
import TooltipContainer from "../TooltipContainer/TooltipContainer";
import FileButton from "../../File/File";
import BtButton from "../../../ts-components/bt-button/bt-button";
import { ReactComponent as ClipIcon } from '../../../assets/svg/clip.svg';
import moment from "moment";
import { FileScope } from "../../../util/appEnum";
import { ScopeOptions } from "../../../models/global.model";
import { NotificationManager } from 'react-notifications';
import { humanizeBytes } from "../../../util";
import { useTranslation } from "react-i18next";
import { useState } from "react";

export type ExistingFile = {
  fileName?: string,
  file?: string,
  mimeType?: string,
  uploadedBy?: string,
  uploadedAt?: Date,
  replacementFile?: File,
  [key: string]: any,
}

export type NewFile = {
  key: string,
  file?: File,
  [key: string]: any,
}

type Props = {
  existingFile?: ExistingFile | null,
  setExistingFile?: (value: ExistingFile | null) => void,
  newFile?: NewFile | null,
  setNewFile?: (value: NewFile | null) => void,
  icon?: ReactNode,
  label?: string,
  inlineButton?: boolean,
  accept?: string,
  readonly?: boolean,
  scope?: FileScope,
  scopeOptions?: ScopeOptions
  maxSize?: number,
  hasRemoveButton?: boolean,
  renderFields?: (file: ExistingFile | NewFile, isExisting: boolean) => ReactNode[],
  allowMetadataOnly?: boolean,
  onSave?: () => Promise<void> | void,
  isSaving?: boolean,
  hasRemoveText?: boolean,
  isDatePickerFocused?: boolean,
}

const FileUploadRow: FC<Props> = ({
  existingFile,
  setExistingFile,
  newFile,
  setNewFile,
  icon,
  label,
  inlineButton,
  accept,
  readonly,
  scope,
  scopeOptions,
  maxSize,
  hasRemoveButton = true,
  renderFields,
  onSave,
  hasRemoveText = false,
  isDatePickerFocused = false,
}) => {
  const [t] = useTranslation('shared', { keyPrefix: 'fileUpload' });
  const [isSaving, setIsSaving] = useState(false);

  const removeFile = () => {
    if (existingFile && setExistingFile) {
      setExistingFile({ ...existingFile, file: undefined, fileName: undefined, replacementFile: undefined });
    } else if (newFile && setNewFile) {
      setNewFile({ ...newFile, file: undefined });
    }
  };

  const addReplacementFile = (file: File) => {
    if (existingFile && setExistingFile) {
      setExistingFile({ ...existingFile, replacementFile: file });
    }
  };

  const removeEntry = () => {
    if (existingFile && setExistingFile) {
      setExistingFile(null);
    } else if (newFile && setNewFile) {
      setNewFile(null);
    }
  };

  const renderFileWithFields = (
    file: ExistingFile | NewFile,
    isExisting: boolean,
    fileButton: ReactNode | null
  ) => {
    const fields = renderFields?.(file, isExisting);
    const hasFields = fields && fields.length > 0;
    const hasButtons = onSave || (hasRemoveButton && !readonly);

    if (!hasFields && !hasButtons) {
      return fileButton;
    }

    const key = isExisting ? (file as ExistingFile).file || 'existing' : (file as NewFile).key;

    return (
      <div key={key} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
        border: '1px solid var(--gray-300)',
        borderRadius: '6px',
        width: '100%',
        backgroundColor: 'var(--gray-50)'
      }}>
        {fileButton}
        {hasFields && fields}
        {hasButtons && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
            {hasRemoveButton && !readonly && (
              <BtButton
                color="white"
                size="xxs"
                style={{ width: 'fit-content' }}
                onClick={(e) => {
                  e.preventDefault();
                  removeEntry();
                }}
              >
                {hasRemoveText ? t('button.remove', 'Ta bort') : t('button.cancel', 'Avboka')}
              </BtButton>
            )}
            {onSave && (
              <BtButton
                color="white"
                size="xxs"
                loaderShow={isSaving}
                style={{ width: 'fit-content' }}
                onClick={async (e) => {
                  e.preventDefault();
                  setIsSaving(true);
                  try {
                    await onSave();
                  } catch (error) {
                    console.error('Failed to save:', error);
                  } finally {
                    setTimeout(() => {
                      setIsSaving(false);
                    }, 500);
                  }
                }}
              >
                {t('button.save', 'Spara')}
              </BtButton>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAddButton = () => (
    <label style={{ letterSpacing: 'normal' }}>
      <BtButton
        color="white"
        size="xxs"
        leftIcon={icon ||
          <ClipIcon style={{ width: '15px', height: '15px', fill: 'var(--gray-700)' }} />
        }
        style={{ width: 'fit-content' }}
      >
        {label || t('label', 'Lägg till bilagor')}
        <input
          type="file"
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            if (target.files && target.files[0]) {
              const file = target.files[0];
              if (maxSize && file.size > maxSize) {
                  NotificationManager.error(
                  t('notification.error.tooBigFiles.description', '{{count}} fil var större än {{maxSize}}.', { count: 1, maxSize: humanizeBytes(maxSize) }),
                    t('notification.error.tooBigFiles.title', 'För stor fil')
                  );
                target.value = "";
                return;
                }
              if (setNewFile) {
                setNewFile({ file, key: `${Date.now()}` });
              }
              if (setExistingFile) {
                setExistingFile(null);
              }
              target.value = "";
            }
          }}
          style={{ display: 'none' }}
          multiple={false}
          accept={accept}
        />
      </BtButton>
    </label>
  );

  const currentFile = existingFile || newFile;

  const shouldRender = currentFile || (!readonly && !inlineButton);

  return (
    <>
      {shouldRender && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: "12px", alignItems: renderFields ? 'stretch' : 'center', flexDirection: renderFields ? 'column' : 'row', width: '100%', zIndex: isDatePickerFocused ? 99999 : 2 }}>
          {existingFile && existingFile.file && !existingFile.replacementFile && (
            renderFileWithFields(existingFile, true, (
              <TooltipContainer
                key={existingFile.file}
                delayed
                renderReferenceComponent={(className, ref) => (
                  <FileButton
                    style={{ placeItems: 'flex-start' }}
                    className={className}
                    ref={ref}
                    file={existingFile.file || ''}
                    fileName={existingFile.fileName || ''}
                    disabled={!existingFile.file}
                    onRemove={!readonly && hasRemoveButton ? removeFile : undefined}
                    scope={scope}
                    scopeOptions={scopeOptions}
                  />
                )}
              >
                {existingFile.uploadedBy && existingFile.uploadedAt && <span style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>{
                  t('fileInfo', 'Tillagd {{timeStamp}} av {{uploadedBy}}', { timeStamp: moment(existingFile.uploadedAt).format('YYYY-MM-DD HH:mm'), uploadedBy: existingFile.uploadedBy })
                }</span>}
              </TooltipContainer>
            ))
          )}
          {existingFile && !existingFile.file && !existingFile.replacementFile && (
            renderFileWithFields(existingFile, true, !readonly ? (
              <label style={{ letterSpacing: 'normal' }}>
                <BtButton
                  color="white"
                  size="xxs"
                  leftIcon={icon ||
                    <ClipIcon style={{ width: '15px', height: '15px', fill: 'var(--gray-700)' }} />
                  }
                  style={{ width: 'fit-content' }}
                >
                  {label || t('label', 'Lägg till bilagor')}
                  <input
                    type="file"
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.files && target.files[0]) {
                        const file = target.files[0];
                        if (maxSize && file.size > maxSize) {
                          NotificationManager.error(
                            t('notification.error.tooBigFiles.description', '{{count}} fil var större än {{maxSize}}.', { count: 1, maxSize: humanizeBytes(maxSize) }),
                            t('notification.error.tooBigFiles.title', 'För stor fil')
                          );
                          target.value = "";
                          return;
                        }
                        addReplacementFile(file);
                        target.value = "";
                      }
                    }}
                    style={{ display: 'none' }}
                    multiple={false}
                    accept={accept}
                  />
                </BtButton>
              </label>
            ) : null)
          )}
          {existingFile && existingFile.replacementFile && (
            renderFileWithFields(existingFile, true, (
              <FileButton
                key={`replacement-${existingFile.replacementFile.name}`}
                file={''}
                blob={existingFile.replacementFile}
                fileName={existingFile.replacementFile.name}
                onRemove={readonly || !hasRemoveButton ? undefined : removeFile}
              />
            ))
          )}
          {newFile && newFile.file && (
            renderFileWithFields(newFile, false, (
              <FileButton
                key={newFile.key}
                file={''}
                blob={newFile.file}
                fileName={newFile.file.name}
                onRemove={readonly || !hasRemoveButton ? undefined : removeFile}
              />
            ))
          )}
          {newFile && !newFile.file && (
            renderFileWithFields(newFile, false, !readonly ? renderAddButton() : null)
          )}
          {!currentFile && !readonly && inlineButton && renderAddButton()}
        </div>
      )}
      {!currentFile && !readonly && !inlineButton && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
          {renderAddButton()}
      </div>
      )}
    </>
  )
}

export default FileUploadRow;
